import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../AuthContext';
import { testLoaderService } from '../services/testLoaderService';
import { progressStore } from '../services/progressStore';
import { TestData, TestAttempt, Question, ChoiceId } from '../types/testy';
import { Spinner } from './LoadingComponents';

interface AnswerReviewProps {
  testId: string;
  onNavigate: (section: string, testId?: string) => void;
}

interface QuestionReviewData {
  question: Question;
  userAnswer?: ChoiceId;
  isCorrect: boolean;
  sectionTitle: string;
  partTitle: string;
  questionNumber: number;
}

const AnswerReview: React.FC<AnswerReviewProps> = ({ testId, onNavigate }) => {
  const { currentUser } = useAuth();
  const [testData, setTestData] = useState<TestData | null>(null);
  const [testAttempt, setTestAttempt] = useState<TestAttempt | null>(null);
  const [reviewData, setReviewData] = useState<QuestionReviewData[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Prepare review data from test and attempt
  const prepareReviewData = useCallback((testData: TestData, attempt: TestAttempt): QuestionReviewData[] => {
    const reviewData: QuestionReviewData[] = [];
    let questionNumber = 1;

    testData.sections.forEach(section => {
      section.parts.forEach(part => {
        part.questions.forEach(question => {
          const userAnswer = attempt.answers[question.id] as ChoiceId | undefined;
          const isCorrect = userAnswer === question.correct;

          reviewData.push({
            question,
            userAnswer,
            isCorrect,
            sectionTitle: section.title,
            partTitle: part.title,
            questionNumber
          });

          questionNumber++;
        });
      });
    });

    return reviewData;
  }, []);

  // Load test data and attempt
  useEffect(() => {
    const loadReviewData = async () => {
      if (!currentUser) return;

      try {
        setLoading(true);
        setError(null);

        // Load test data
        const data = await testLoaderService.loadTest(testId);
        setTestData(data);

        // Load the most recent completed attempt
        const history = await progressStore.getTestHistory(currentUser.uid);
        const completedAttempt = history.find(attempt => 
          attempt.testId === testId && attempt.isCompleted
        );

        if (!completedAttempt) {
          throw new Error('No completed test attempt found');
        }

        setTestAttempt(completedAttempt);

        // Prepare review data
        const reviewQuestions = prepareReviewData(data, completedAttempt);
        setReviewData(reviewQuestions);

      } catch (err) {
        console.error('Failed to load review data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load review data');
      } finally {
        setLoading(false);
      }
    };

    loadReviewData();
  }, [testId, currentUser, prepareReviewData]);

  // Navigation functions
  const goToQuestion = useCallback((index: number) => {
    if (index >= 0 && index < reviewData.length) {
      setCurrentQuestionIndex(index);
    }
  }, [reviewData.length]);

  const goToPrevious = useCallback(() => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  }, [currentQuestionIndex]);

  const goToNext = useCallback(() => {
    if (currentQuestionIndex < reviewData.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  }, [currentQuestionIndex, reviewData.length]);

  // Get statistics
  const getStatistics = () => {
    const total = reviewData.length;
    const correct = reviewData.filter(item => item.isCorrect).length;
    const incorrect = reviewData.filter(item => item.userAnswer && !item.isCorrect).length;
    const unanswered = reviewData.filter(item => !item.userAnswer).length;

    return { total, correct, incorrect, unanswered };
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4">⚠️</div>
        <h2 className="text-2xl font-semibold text-red-600 dark:text-red-400 mb-2 font-poppins">
          Chyba při načítání přehledu odpovědí
        </h2>
        <p className="text-light-text-secondary dark:text-dark-text-secondary font-poppins mb-4">
          {error}
        </p>
        <button
          onClick={() => onNavigate('test-list')}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-lg font-poppins font-medium transition-colors duration-200"
        >
          Zpět na seznam testů
        </button>
      </div>
    );
  }

  if (!testData || !testAttempt || reviewData.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4">❓</div>
        <h2 className="text-2xl font-semibold text-light-text-primary dark:text-dark-text-primary mb-2 font-poppins">
          Přehled odpovědí nenalezen
        </h2>
        <p className="text-light-text-secondary dark:text-dark-text-secondary font-poppins mb-4">
          Přehled odpovědí pro tento test není dostupný.
        </p>
        <button
          onClick={() => onNavigate('test-list')}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-lg font-poppins font-medium transition-colors duration-200"
        >
          Zpět na seznam testů
        </button>
      </div>
    );
  }

  const currentReview = reviewData[currentQuestionIndex];
  const statistics = getStatistics();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header 
        className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10"
        role="banner"
        aria-label="Answer review header"
      >
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            {/* Test info */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4">
              <h1 className="text-base sm:text-lg font-semibold text-light-text-primary dark:text-dark-text-primary font-poppins">
                Přehled odpovědí: {testData.title}
              </h1>
              <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                Otázka {currentQuestionIndex + 1} z {reviewData.length}
              </div>
            </div>

            {/* Statistics */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 w-full sm:w-auto">
              <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm">
                <div className="flex items-center">
                  <div 
                    className="w-3 h-3 bg-green-500 rounded-full mr-2"
                    aria-hidden="true"
                  ></div>
                  <span className="text-light-text-secondary dark:text-dark-text-secondary">
                    <span className="sr-only">Správných odpovědí: </span>
                    <span className="hidden sm:inline">Správně: </span>
                    {statistics.correct}
                  </span>
                </div>
                <div className="flex items-center">
                  <div 
                    className="w-3 h-3 bg-red-500 rounded-full mr-2"
                    aria-hidden="true"
                  ></div>
                  <span className="text-light-text-secondary dark:text-dark-text-secondary">
                    <span className="sr-only">Špatných odpovědí: </span>
                    <span className="hidden sm:inline">Špatně: </span>
                    {statistics.incorrect}
                  </span>
                </div>
                <div className="flex items-center">
                  <div 
                    className="w-3 h-3 bg-gray-400 rounded-full mr-2"
                    aria-hidden="true"
                  ></div>
                  <span className="text-light-text-secondary dark:text-dark-text-secondary">
                    <span className="sr-only">Nevyplněných odpovědí: </span>
                    <span className="hidden sm:inline">Nevyplněno: </span>
                    {statistics.unanswered}
                  </span>
                </div>
              </div>

              {/* Success rate */}
              <div 
                className="text-lg font-semibold text-light-text-primary dark:text-dark-text-primary"
                aria-label={`Celková úspěšnost: ${((statistics.correct / statistics.total) * 100).toFixed(1)} procent`}
              >
                {((statistics.correct / statistics.total) * 100).toFixed(1)}%
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-4">
        <div className="grid lg:grid-cols-4 gap-6">
          {/* Main question review area */}
          <main className="lg:col-span-3" role="main" aria-label="Question review">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
              {/* Question header with status */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3">
                  <span className="text-sm font-medium text-light-text-secondary dark:text-dark-text-secondary">
                    Otázka {currentReview.questionNumber}
                  </span>
                  <div className="text-xs text-light-text-secondary dark:text-dark-text-secondary mt-1 sm:mt-0">
                    {currentReview.sectionTitle} • {currentReview.partTitle}
                  </div>
                </div>
                
                {/* Status indicator */}
                <div 
                  className={`flex items-center px-3 py-1 rounded-full text-sm font-medium self-start ${
                    !currentReview.userAnswer
                      ? 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                      : currentReview.isCorrect
                        ? 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                        : 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400'
                  }`}
                  role="status"
                  aria-label={
                    !currentReview.userAnswer 
                      ? 'Otázka nebyla zodpovězena'
                      : currentReview.isCorrect 
                        ? 'Odpověď je správná'
                        : 'Odpověď je nesprávná'
                  }
                >
                  {!currentReview.userAnswer ? (
                    <>
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      <span>Nevyplněno</span>
                    </>
                  ) : currentReview.isCorrect ? (
                    <>
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span>Správně</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      <span>Nesprávně</span>
                    </>
                  )}
                </div>
              </div>

              {/* Question stem */}
              <div className="mb-6">
                <div 
                  className="text-base sm:text-lg text-light-text-primary dark:text-dark-text-primary leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: currentReview.question.stemMd }}
                  role="heading"
                  aria-level={2}
                />
              </div>

              {/* Answer choices with indicators */}
              <div className="space-y-3 mb-6">
                {currentReview.question.choices.map((choice) => {
                  const isUserAnswer = currentReview.userAnswer === choice.id;
                  const isCorrectAnswer = currentReview.question.correct === choice.id;
                  
                  let choiceStyle = 'border-gray-200 dark:border-gray-600';
                  let iconElement = null;
                  
                  if (isCorrectAnswer) {
                    choiceStyle = 'border-green-500 bg-green-50 dark:bg-green-900/20';
                    iconElement = (
                      <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    );
                  } else if (isUserAnswer && !isCorrectAnswer) {
                    choiceStyle = 'border-red-500 bg-red-50 dark:bg-red-900/20';
                    iconElement = (
                      <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    );
                  }

                  return (
                    <div
                      key={choice.id}
                      className={`flex items-start p-4 rounded-lg border-2 ${choiceStyle}`}
                    >
                      <div className="flex items-center mr-4">
                        <span className="font-semibold text-light-text-primary dark:text-dark-text-primary mr-2">
                          {choice.id}.
                        </span>
                        {iconElement}
                      </div>
                      <div className="flex-1">
                        <div 
                          className="text-light-text-primary dark:text-dark-text-primary"
                          dangerouslySetInnerHTML={{ __html: choice.textMd }}
                        />
                        {isUserAnswer && (
                          <div className="mt-2 text-sm text-light-text-secondary dark:text-dark-text-secondary">
                            Vaše odpověď
                          </div>
                        )}
                        {isCorrectAnswer && (
                          <div className="mt-2 text-sm font-medium text-green-600 dark:text-green-400">
                            Správná odpověď
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Answer summary */}
              <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <h4 className="font-medium text-light-text-primary dark:text-dark-text-primary mb-2">
                  Shrnutí odpovědi
                </h4>
                <div className="text-sm space-y-1">
                  <div className="flex justify-between">
                    <span>Vaše odpověď:</span>
                    <span className={`font-medium ${
                      !currentReview.userAnswer 
                        ? 'text-gray-500 dark:text-gray-400'
                        : currentReview.isCorrect
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-red-600 dark:text-red-400'
                    }`}>
                      {currentReview.userAnswer || 'Nevyplněno'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Správná odpověď:</span>
                    <span className="font-medium text-green-600 dark:text-green-400">
                      {currentReview.question.correct}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Výsledek:</span>
                    <span className={`font-medium ${
                      !currentReview.userAnswer 
                        ? 'text-gray-500 dark:text-gray-400'
                        : currentReview.isCorrect
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-red-600 dark:text-red-400'
                    }`}>
                      {!currentReview.userAnswer 
                        ? '0 bodů (nevyplněno)'
                        : currentReview.isCorrect
                          ? '+1 bod'
                          : '-0.33 bodu'
                      }
                    </span>
                  </div>
                </div>
              </div>

              {/* Explanation */}
              {currentReview.question.explanationMd && (
                <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2 flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    Vysvětlení
                  </h4>
                  <div 
                    className="text-blue-800 dark:text-blue-200 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: currentReview.question.explanationMd }}
                  />
                </div>
              )}

              {/* Navigation buttons */}
              <div className="flex items-center justify-between pt-6 border-t border-gray-200 dark:border-gray-600">
                <button
                  onClick={goToPrevious}
                  disabled={currentQuestionIndex === 0}
                  className="flex items-center px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Předchozí
                </button>

                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => onNavigate('test-summary', testId)}
                    className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg font-medium transition-colors duration-200"
                  >
                    Zpět k výsledkům
                  </button>
                  
                  <button
                    onClick={() => onNavigate('test-list')}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-lg font-medium transition-colors duration-200"
                  >
                    Seznam testů
                  </button>
                </div>

                <button
                  onClick={goToNext}
                  disabled={currentQuestionIndex === reviewData.length - 1}
                  className="flex items-center px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  Další
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </main>

          {/* Question navigation sidebar */}
          <aside className="lg:col-span-1" aria-label="Question navigation">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 sticky top-24">
              <h3 className="font-semibold text-light-text-primary dark:text-dark-text-primary mb-4">
                Navigace otázek
              </h3>
              
              {/* Statistics summary */}
              <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg text-sm">
                <div className="flex justify-between mb-1">
                  <span>Správně:</span>
                  <span className="font-medium text-green-600 dark:text-green-400">{statistics.correct}</span>
                </div>
                <div className="flex justify-between mb-1">
                  <span>Špatně:</span>
                  <span className="font-medium text-red-600 dark:text-red-400">{statistics.incorrect}</span>
                </div>
                <div className="flex justify-between">
                  <span>Nevyplněno:</span>
                  <span className="font-medium text-gray-500 dark:text-gray-400">{statistics.unanswered}</span>
                </div>
              </div>

              {/* Question grid */}
              <div className="grid grid-cols-5 gap-2">
                {reviewData.map((review, index) => {
                  let buttonStyle = 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400';
                  
                  if (!review.userAnswer) {
                    buttonStyle = 'bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-400';
                  } else if (review.isCorrect) {
                    buttonStyle = 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400';
                  } else {
                    buttonStyle = 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400';
                  }

                  if (index === currentQuestionIndex) {
                    buttonStyle += ' ring-2 ring-blue-500 dark:ring-blue-400';
                  }

                  return (
                    <button
                      key={index}
                      onClick={() => goToQuestion(index)}
                      className={`w-10 h-10 rounded-lg text-sm font-medium transition-all duration-200 hover:scale-105 ${buttonStyle}`}
                      title={`Otázka ${review.questionNumber}: ${review.isCorrect ? 'Správně' : review.userAnswer ? 'Špatně' : 'Nevyplněno'}`}
                    >
                      {review.questionNumber}
                    </button>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="mt-4 text-xs space-y-2">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-100 dark:bg-green-900/30 rounded mr-2"></div>
                  <span className="text-light-text-secondary dark:text-dark-text-secondary">Správně</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-red-100 dark:bg-red-900/30 rounded mr-2"></div>
                  <span className="text-light-text-secondary dark:text-dark-text-secondary">Špatně</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-gray-200 dark:bg-gray-600 rounded mr-2"></div>
                  <span className="text-light-text-secondary dark:text-dark-text-secondary">Nevyplněno</span>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default AnswerReview;